import React, { useEffect, useState } from 'react';
import axios from 'axios';
import LoadingSpinner from '../LoadingSpinner';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const KIND_LABELS = {
  missing_tool: 'Missing tool',
  missing_sdk: 'Missing SDK method',
  missing_library: 'Missing library',
  missing_primitive: 'Missing engine primitive',
};

function canSendManually(row) {
  return row.developmentStatus === 'pending' || row.developmentStatus === 'skipped';
}

const AgentInsightsTab = ({ auditId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [insights, setInsights] = useState([]);
  const [sendingId, setSendingId] = useState('');
  const [sendErrors, setSendErrors] = useState({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    axios.get(`${API_BASE_URL}/api/admin/ai-customizations/${auditId}/insights`)
      .then((response) => {
        if (cancelled) return;
        setInsights(response.data?.data?.insights || []);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load agent insights');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [auditId]);

  const sendForDevelopment = async (row) => {
    if (!canSendManually(row) || sendingId) return;
    const confirmed = window.confirm(
      `Send "${row.wanted}" to the development agent now? This opens a task even when automatic sending left it on this tab.`
    );
    if (!confirmed) return;
    setSendingId(row.id);
    setSendErrors((current) => ({ ...current, [row.id]: '' }));
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/ai-customizations/${auditId}/insights/${row.id}/send`
      );
      const updated = response.data?.data?.insight;
      if (updated) {
        setInsights((current) => current.map((item) => (item.id === row.id ? updated : item)));
      }
    } catch (err) {
      const message = err?.response?.data?.error || 'Failed to send this insight for development';
      setSendErrors((current) => ({ ...current, [row.id]: message }));
    } finally {
      setSendingId('');
    }
  };

  if (loading) {
    return (
      <div className="py-12 flex justify-center">
        <LoadingSpinner />
      </div>
    );
  }
  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!insights.length) {
    return <p className="text-sm text-gray-500">This prompt reported no missing capability.</p>;
  }

  return (
    <div className="space-y-4">
      {insights.map((row) => (
        <article key={row.id} className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-xs font-medium uppercase tracking-wide text-amber-800 bg-amber-100 px-2 py-0.5 rounded">
              {KIND_LABELS[row.kind] || row.kind}
            </span>
            <code className="text-sm text-gray-900">{row.wanted}</code>
          </div>
          <p className="text-sm text-gray-900">{row.summary}</p>
          {row.detail ? (
            <p className="mt-2 text-sm text-gray-600 whitespace-pre-wrap">{row.detail}</p>
          ) : null}
          {row.workaround ? (
            <p className="mt-2 text-sm text-gray-700">
              <span className="font-medium">Workaround: </span>
              {row.workaround}
            </p>
          ) : null}
          {row.developmentStatus === 'sent' ? (
            <p className="mt-2 text-sm text-green-700">
              <span className="font-medium">Sent for development</span>
              {row.developmentTaskId ? <span className="text-gray-600"> · {row.developmentTaskId}</span> : null}
            </p>
          ) : row.developmentStatus === 'skipped' ? (
            <p className="mt-2 text-sm text-gray-600">
              <span className="font-medium">Not sent</span>
              {row.developmentNote ? ` · ${row.developmentNote}` : ''}
            </p>
          ) : row.developmentStatus === 'queued' ? (
            <p className="mt-2 text-sm font-medium text-amber-700">Queued</p>
          ) : (
            <p className="mt-2 text-sm text-gray-500">Not sent for development</p>
          )}
          {canSendManually(row) ? (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => sendForDevelopment(row)}
                disabled={Boolean(sendingId)}
                className="inline-flex items-center rounded-md bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
              >
                {sendingId === row.id ? 'Sending…' : 'Send for development'}
              </button>
              {sendErrors[row.id] ? (
                <p className="mt-2 text-sm text-red-600">{sendErrors[row.id]}</p>
              ) : null}
            </div>
          ) : null}
        </article>
      ))}
    </div>
  );
};

export default AgentInsightsTab;
